import QRCode from "qrcode";

export const TOTP_ISSUER = "Taibah University Conferences" as const;

const ALLOWED_TOTP_PARAMETERS = new Set([
  "algorithm",
  "digits",
  "issuer",
  "period",
  "secret",
]);
const BASE32_SECRET_PATTERN = /^[A-Z2-7]+={0,6}$/;
const MAX_SETUP_URI_LENGTH = 4_096;
const MAX_ACCOUNT_LABEL_LENGTH = 320;

export type TotpQrErrorReason =
  | "invalid_setup_uri"
  | "unsupported_otp_type"
  | "invalid_issuer"
  | "invalid_account_label"
  | "invalid_secret"
  | "unsupported_totp_parameters"
  | "qr_generation_failed";

/**
 * Deliberately contains only a stable reason code. Never attach the setup URI,
 * its secret, or the underlying renderer error to a user-visible error.
 */
export class TotpQrError extends Error {
  readonly reasonCode: TotpQrErrorReason;

  constructor(reasonCode: TotpQrErrorReason) {
    super(reasonCode);
    this.name = "TotpQrError";
    this.reasonCode = reasonCode;
  }
}

export interface TotpEnrollmentMetadata {
  readonly issuer: typeof TOTP_ISSUER;
  readonly accountLabel: string;
  readonly algorithm: "SHA1";
  readonly digits: 6;
  readonly periodSeconds: 30;
}

interface ValidatedTotpEnrollment {
  readonly canonicalUri: string;
  readonly metadata: TotpEnrollmentMetadata;
}

function hasControlCharacter(value: string) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function normalizedExpectedAccountLabel(expectedAccountLabel: string) {
  if (
    typeof expectedAccountLabel !== "string" ||
    expectedAccountLabel.length === 0 ||
    expectedAccountLabel.length > MAX_ACCOUNT_LABEL_LENGTH ||
    expectedAccountLabel !== expectedAccountLabel.trim() ||
    hasControlCharacter(expectedAccountLabel)
  ) {
    throw new TotpQrError("invalid_account_label");
  }
  return expectedAccountLabel;
}

function parseSetupUri(
  setupUri: string,
  expectedAccountLabel: string,
): ValidatedTotpEnrollment {
  const expectedLabel = normalizedExpectedAccountLabel(expectedAccountLabel);
  if (
    typeof setupUri !== "string" ||
    setupUri.length === 0 ||
    setupUri.length > MAX_SETUP_URI_LENGTH ||
    setupUri !== setupUri.trim() ||
    hasControlCharacter(setupUri)
  ) {
    throw new TotpQrError("invalid_setup_uri");
  }

  let uri: URL;
  try {
    uri = new URL(setupUri);
  } catch {
    throw new TotpQrError("invalid_setup_uri");
  }

  if (uri.protocol !== "otpauth:" || uri.hostname !== "totp") {
    throw new TotpQrError("unsupported_otp_type");
  }
  if (uri.username || uri.password || uri.port || uri.hash) {
    throw new TotpQrError("invalid_setup_uri");
  }

  for (const parameter of uri.searchParams.keys()) {
    if (!ALLOWED_TOTP_PARAMETERS.has(parameter)) {
      throw new TotpQrError("unsupported_totp_parameters");
    }
  }
  for (const parameter of ALLOWED_TOTP_PARAMETERS) {
    if (uri.searchParams.getAll(parameter).length > 1) {
      throw new TotpQrError("unsupported_totp_parameters");
    }
  }

  const issuer = uri.searchParams.get("issuer");
  if (issuer !== TOTP_ISSUER) {
    throw new TotpQrError("invalid_issuer");
  }

  let decodedPathLabel: string;
  try {
    decodedPathLabel = decodeURIComponent(uri.pathname.replace(/^\//, ""));
  } catch {
    throw new TotpQrError("invalid_account_label");
  }
  if (decodedPathLabel !== `${TOTP_ISSUER}:${expectedLabel}`) {
    throw new TotpQrError(
      decodedPathLabel.startsWith(`${TOTP_ISSUER}:`)
        ? "invalid_account_label"
        : "invalid_issuer",
    );
  }

  const secret = uri.searchParams.get("secret");
  const unpaddedSecretLength = secret?.replace(/=+$/, "").length ?? 0;
  if (
    !secret ||
    !BASE32_SECRET_PATTERN.test(secret) ||
    unpaddedSecretLength < 16 ||
    unpaddedSecretLength > 128
  ) {
    throw new TotpQrError("invalid_secret");
  }

  const algorithm = uri.searchParams.get("algorithm") ?? "SHA1";
  const digits = uri.searchParams.get("digits") ?? "6";
  const period = uri.searchParams.get("period") ?? "30";
  if (algorithm !== "SHA1" || digits !== "6" || period !== "30") {
    throw new TotpQrError("unsupported_totp_parameters");
  }

  return Object.freeze({
    canonicalUri: uri.toString(),
    metadata: Object.freeze({
      issuer: TOTP_ISSUER,
      accountLabel: expectedLabel,
      algorithm: "SHA1",
      digits: 6,
      periodSeconds: 30,
    }),
  });
}

export function validateTotpEnrollmentUri(
  setupUri: string,
  expectedAccountLabel: string,
): TotpEnrollmentMetadata {
  return parseSetupUri(setupUri, expectedAccountLabel).metadata;
}

export async function generateTotpQrSvg(
  setupUri: string,
  expectedAccountLabel: string,
): Promise<string> {
  const enrollment = parseSetupUri(setupUri, expectedAccountLabel);
  try {
    return await QRCode.toString(enrollment.canonicalUri, {
      type: "svg",
      errorCorrectionLevel: "M",
      margin: 2,
      color: {
        dark: "#071726ff",
        light: "#ffffffff",
      },
    });
  } catch {
    throw new TotpQrError("qr_generation_failed");
  }
}

export async function generateTotpQrDataUri(
  setupUri: string,
  expectedAccountLabel: string,
): Promise<string> {
  const svg = await generateTotpQrSvg(setupUri, expectedAccountLabel);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
